'use client'

import {Radio} from 'antd'
import {observer} from 'mobx-react'
import {useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {Shop} from '@/src/entities/shop'

import {FormTemplate} from '@/src/shared/modules/form/ui/template'
import {DeliveryMapBlockLazy} from '@/src/features/common/yandex-map/ui/delivery-map-block-lazy'
import {useFormTesting} from '@/src/features/functional-testing'
import {FormTestingStep} from '@/src/features/functional-testing/store'

import mapIcon from '@/src/shared/assets/media/map-icon.png'

import {useStore} from '@/src/shared/hooks/useStore'
import {Collapse} from '@/src/shared/ui/collapse'

import {ShopItem} from './ShopItem'
import * as UI from './shop-form.styled'
import {SelectType, ShopFormData, ShopFormSchema} from './shop-form.types'
import {Point} from '@/src/features/common/yandex-map/types'

export {ShopFormSchema}
export type {ShopFormData}

/**
 * Shop selection form for testing
 *
 * Responsibilities:
 * - Display shops as list or on map
 * - Handle shop selection
 * - Progress to next form step
 *
 * Uses extracted components:
 * - ShopItem: List item display
 */
export const ShopForm = observer(() => {
  const [selectType, setSelectType] = useState<string>(SelectType.List)
  const [showMap, setShowMap] = useState(true)

  const form = useFormContext<ShopFormData>()
  const testingFormStore = useFormTesting()

  const {pageData} = useStore<{labs: Shop[]}>()
  const {labs} = pageData!.content

  const shopId = form.watch('lab_id')
  const selectedShop = labs.find(lab => lab.id === shopId)

  const handleSubmit = form.handleSubmit(data => {
    testingFormStore.setShopId(data.lab_id)
    testingFormStore.advance(FormTestingStep.SelectDay)
  })

  const handleSelect = (shop: Shop) => {
    form.setValue('lab_id', shop.id)
    handleSubmit()
  }

  const validLabs = labs.filter(
    lab => !!lab.latitude && !!lab.longitude && parseFloat(lab.latitude) && parseFloat(lab.longitude),
  ).map(lab => {
    return {
      ...lab,
      latitude: lab.latitude ? parseFloat(lab.latitude): 0,
      longitude: lab.longitude ? parseFloat(lab.longitude): 0,
    } satisfies Point
  })

  return (
    <FormTemplate
      title={
        <UI.ShopFormTitleWrapper>
          <span>Выберите Лабораторию:</span>
          <Radio.Group
            name="select-type"
            value={selectType}
            onChange={e => setSelectType(e.target.value)}
            options={[
              {label: 'Списком', value: SelectType.List},
              {label: 'На карте', value: SelectType.Map},
            ]}
            block
            optionType="button"
            buttonStyle="solid"
          />
        </UI.ShopFormTitleWrapper>
      }>
      {selectType === SelectType.List ? (
        <UI.ShopList>
          {labs.map(shop => (
            <ShopItem key={shop.id} onSelect={() => handleSelect(shop)} shop={shop} active={shop.id === shopId} />
          ))}
        </UI.ShopList>
      ) : (
        <UI.Map>
          <Collapse
            onChange={key => {
              if (key) setShowMap(true)
            }}
            items={[
              {
                key: '1',
                label: (
                  <UI.SelectedLab>
                    {selectedShop ? (
                      <span>
                        Выбрано: {selectedShop.title}, {selectedShop.address}
                        {selectedShop.work_schedule && `, ${selectedShop.work_schedule}`}
                      </span>
                    ) : (
                      <span>Выберите точку на карте</span>
                    )}
                  </UI.SelectedLab>
                ),
                children: (
                  <DeliveryMapBlockLazy
                    points={validLabs}
                    onSelect={pickupPointId => {
                      const lab = labs.find(lab => lab.id === pickupPointId)
                      if (lab) {
                        handleSelect(lab)
                        setShowMap(false)
                      }
                    }}
                    icon={{href: mapIcon.src, size: [40, 40], offset: [-20, -20]}}
                  />
                ),
              },
            ]}
            activeKey={showMap ? ['1'] : []}
          />
        </UI.Map>
      )}
    </FormTemplate>
  )
})
